import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText,
  Edit,
  Trash2,
  Copy,
  Plus,
  Calendar,
  Loader2,
  Star
} from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { toast } from 'sonner';
import { preApprovalTemplateService } from '@/services/preApprovalTemplateService';
import { userPreferencesService } from '@/services/userPreferencesService';
import { TemplatePreviewThumbnail } from './TemplatePreviewThumbnail';
import { supabase } from '@/utils/supabase';
import { useAuth } from '@/contexts/useAuth';

import logger from '@/utils/logger';
interface TemplateLayout {
  id: string;
  name: string;
  pageSize: 'letter' | 'a4';
  orientation: 'portrait' | 'landscape';
  margins: {
    top: number;
    bottom: number;
    left: number;
    right: number;
  };
  elements: Array<{
    id: string;
    type: 'text' | 'image' | 'divider' | 'signature';
    content?: string;
    imageUrl?: string;
    x: number;
    y: number;
    width: number;
    height: number;
    styles: {
      fontSize: number;
      fontFamily: string;
      fontWeight: 'normal' | 'bold';
      fontStyle: 'normal' | 'italic';
      textDecoration: 'none' | 'underline';
      textAlign: 'left' | 'center' | 'right';
      color: string;
      backgroundColor?: string;
      padding: number;
      borderRadius: number;
      border?: string;
      lineHeight?: number;
    };
  }>;
  createdAt: string;
  updatedAt: string;
  isDefault?: boolean;
  isCorporateDefault?: boolean;
  isOrganizationDefault?: boolean;
}

interface VisualTemplateGalleryProps {
  onSelectTemplate: (template: TemplateLayout) => void;
  onCreateNew: () => void;
  autoSelectDefault?: boolean;
}

export const VisualTemplateGallery: React.FC<VisualTemplateGalleryProps> = ({
  onSelectTemplate,
  onCreateNew,
  autoSelectDefault = false
}) => {
  const { user } = useAuth();
  const [templates, setTemplates] = useState<TemplateLayout[]>([]);
  const [deleteTemplate, setDeleteTemplate] = useState<TemplateLayout | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [userDefaultTemplateId, setUserDefaultTemplateId] = useState<string | undefined>(undefined);

  const loadTemplates = useCallback(async () => {
    setIsLoading(true);
    try {
      // Load user preferences to get their default template
      const { data: userPrefs } = await userPreferencesService.getPreferences();
      const userDefaultId = userPrefs?.workflowPreferences?.defaultPreApprovalTemplateId;
      setUserDefaultTemplateId(userDefaultId);

      // Load templates from database
      const { data: dbTemplates, error } = await preApprovalTemplateService.loadTemplates();

      if (error) {
        logger.error('Error loading templates from database:', error);
        toast.error('Failed to load templates from database. Using local backup.');

        // Fallback to localStorage only if database fails
        const savedTemplates = JSON.parse(localStorage.getItem('visual-templates') || '[]');
        setTemplates(savedTemplates);
      } else if (dbTemplates) {
        // Convert database templates to TemplateLayout format
        const convertedTemplates: TemplateLayout[] = dbTemplates
          .filter(template => template.style_config) // Ensure style_config exists
          .map(template => {
            // Type cast style_config to access its properties properly
            const styleConfig = template.style_config as {
              pageSize?: 'letter' | 'a4';
              orientation?: 'portrait' | 'landscape';
              margins?: { top: number; bottom: number; left: number; right: number };
              elements?: Array<any>;
            };

            const converted = {
              id: template.id || `template-${Date.now()}`,
              name: template.name,
              pageSize: styleConfig.pageSize || 'letter',
              orientation: styleConfig.orientation || 'portrait',
              margins: styleConfig.margins || { top: 40, bottom: 40, left: 40, right: 40 },
              elements: styleConfig.elements || [], // Default to empty array if no elements
              createdAt: template.created_at || new Date().toISOString(),
              updatedAt: template.updated_at || new Date().toISOString(),
              isDefault: template.is_organization_default || template.is_corporate_default || false,
              isCorporateDefault: template.is_corporate_default || false,
              isOrganizationDefault: template.is_organization_default || false
            };

            return converted;
          });
        
        setTemplates(convertedTemplates);
        
        // Update localStorage with database templates for offline access
        localStorage.setItem('visual-templates', JSON.stringify(convertedTemplates));
        
        // Auto-select default template if flag is set
        if (autoSelectDefault && convertedTemplates.length > 0) {
          // First priority: user's personal default
          if (userDefaultId) {
            const userDefault = convertedTemplates.find(t => t.id === userDefaultId);
            if (userDefault) {
              onSelectTemplate(userDefault);
              return;
            } else {
              // User's default template was deleted - clear the stale preference
              logger.warn('User default template not found, clearing stale preference', {
                templateId: userDefaultId
              });
              userPreferencesService.updateWorkflowPreferences({
                defaultPreApprovalTemplateId: undefined
              }).catch(err => {
                logger.error('Failed to clear stale template preference:', err);
              });
              toast.warning('Your default template was deleted. Please select a new one.');
            }
          }

          // Second priority: organization default (if no user preference)
          const orgDefault = convertedTemplates.find(t => t.isOrganizationDefault);
          if (orgDefault) {
            onSelectTemplate(orgDefault);
            return;
          }

          // Third priority: corporate default
          const corpDefault = convertedTemplates.find(t => t.isCorporateDefault);
          if (corpDefault) {
            onSelectTemplate(corpDefault);
            return;
          }

          // Fourth priority: first available template
          onSelectTemplate(convertedTemplates[0]);
        }
      }
    } catch (error) {
      logger.error('Unexpected error loading templates:', error);
      toast.error('Failed to load templates');
      
      // Last resort fallback
      const savedTemplates = JSON.parse(localStorage.getItem('visual-templates') || '[]');
      setTemplates(savedTemplates);
    } finally {
      setIsLoading(false);
    }
  }, [autoSelectDefault, onSelectTemplate]);
  
  useEffect(() => {
    loadTemplates();
  }, []);

  const handleDuplicate = async (template: TemplateLayout, e: React.MouseEvent) => {
    e.stopPropagation();
    
    try {
      const { error } = await preApprovalTemplateService.cloneTemplate(
        template.id,
        `${template.name} (Copy)`
      );
      
      if (error) {
        toast.error(`Failed to duplicate template: ${error.message}`);
      } else {
        toast.success('Template duplicated successfully');
        // Reload templates to show the new duplicate
        await loadTemplates();
      }
    } catch (error) {
      logger.error('Error duplicating template:', error);
      toast.error('Failed to duplicate template');
    }
  };

  const handleDelete = (template: TemplateLayout, e: React.MouseEvent) => {
    e.stopPropagation();
    setDeleteTemplate(template);
  };

  const confirmDelete = async () => {
    if (!deleteTemplate) {
return;
}

    try {
      const { error } = await preApprovalTemplateService.deleteTemplate(deleteTemplate.id);
      
      if (error) {
        toast.error(`Failed to delete template: ${error.message}`);
      } else {
        toast.success('Template deleted successfully');
        // Reload templates to reflect the deletion
        await loadTemplates();
      }
    } catch (error) {
      logger.error('Error deleting template:', error);
      toast.error('Failed to delete template');
    } finally {
      setDeleteTemplate(null);
    }
  };

  const handleSetAsDefault = async (template: TemplateLayout, e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      // First, validate that the template exists and user has access at database level
      const { data: validTemplate, error: validationError } = await supabase
        .from('pre_approval_templates')
        .select('id, organization_id, is_corporate_default')
        .eq('id', template.id)
        .single();

      if (validationError || !validTemplate) {
        logger.error('Template validation failed:', validationError);
        toast.error('Template not found or access denied');
        return;
      }

      // Get user's organization to verify access
      const { data: userProfile } = await supabase
        .from('users')
        .select('organization_id')
        .eq('id', user?.id)
        .single();

      // Check if user has access (either same org or corporate default)
      const hasAccess = validTemplate.organization_id === userProfile?.organization_id ||
                       validTemplate.is_corporate_default === true;

      if (!hasAccess) {
        logger.warn('User attempted to set inaccessible template as default', {
          templateId: template.id,
          userOrg: userProfile?.organization_id,
          templateOrg: validTemplate.organization_id
        });
        toast.error('You do not have access to this template');
        return;
      }

      // Now update user's personal default template preference
      const { error } = await userPreferencesService.updateWorkflowPreferences({
        defaultPreApprovalTemplateId: template.id
      });

      if (error) {
        toast.error(`Failed to set default template: ${error.message}`);
      } else {
        setUserDefaultTemplateId(template.id);
        toast.success('Your default template has been updated');
        // Reload templates to reflect the change
        await loadTemplates();
      }
    } catch (error) {
      logger.error('Error setting user default template:', error);
      toast.error('Failed to set your default template');
    }
  };

  const handleUnsetDefault = async (e: React.MouseEvent) => {
    e.stopPropagation();

    try {
      // Clear user's personal default template preference
      const { error } = await userPreferencesService.updateWorkflowPreferences({
        defaultPreApprovalTemplateId: undefined
      });

      if (error) {
        toast.error(`Failed to unset default template: ${error.message}`);
      } else {
        setUserDefaultTemplateId(undefined);
        toast.success('Your default template has been cleared');
        // Reload templates to reflect the change
        await loadTemplates();
      }
    } catch (error) {
      logger.error('Error unsetting user default template:', error);
      toast.error('Failed to clear your default template');
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric'
    });
  };

  if (isLoading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader2 className="w-8 h-8 animate-spin text-gray-400" />
        <span className="ml-2 text-gray-600">Loading templates...</span>
      </div>
    );
  }

  // Separate different types of templates
  const corporateDefault = templates.find(t => t.isCorporateDefault);
  const organizationDefault = templates.find(t => t.isOrganizationDefault && !t.isCorporateDefault);
  
  // Active templates include corporate default (always) + user's org default (if set)
  const activeTemplates = [
    ...(corporateDefault ? [corporateDefault] : []),
    ...(organizationDefault ? [organizationDefault] : [])
  ];
  
  // Other templates are everything else (not corporate default, not user's org default)
  const otherTemplates = templates.filter(t => !t.isCorporateDefault && !t.isOrganizationDefault);

  return (
    <>
      <div className="space-y-6">
        {/* Active Templates Section */}
        {activeTemplates.length > 0 && (
          <div>
            <div className="flex items-center justify-between mb-3">
              <h2 className="text-lg font-semibold text-gray-900">Active Templates</h2>
              {organizationDefault && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleUnsetDefault}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                >
                  Unset Your Default
                </Button>
              )}
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {activeTemplates.map((template) => (
              <Card
                key={template.id}
                className="hover:shadow-xl hover:-translate-y-1 transition-all duration-200 ease-out cursor-pointer ring-2 ring-[#183C2E] ring-offset-2"
                onClick={() => {
                  onSelectTemplate(template);
                }}
              >
                <CardHeader className="pb-4">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-2">
                        <CardTitle className="text-base line-clamp-1" title={template.name}>
                          {template.name}
                        </CardTitle>
                        {template.isCorporateDefault && (
                          <Badge className="bg-[#183C2E] text-white text-xs">
                            CORPORATE DEFAULT
                          </Badge>
                        )}
                        {template.isOrganizationDefault && (
                          <Badge className="bg-orange-600 text-white text-xs">
                            ORGANIZATION DEFAULT
                          </Badge>
                        )}
                        {userDefaultTemplateId === template.id && (
                          <Badge className="bg-blue-600 text-white text-xs">
                            MY DEFAULT
                          </Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-2 mt-1">
                        <Badge variant="secondary" className="text-xs font-mono">
                          ID: {template.id.substring(0, 8)}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {template.pageSize === 'letter' ? '8.5" × 11"' : 'A4'}
                        </Badge>
                        <Badge variant="outline" className="text-xs">
                          {template.elements.length} elements
                        </Badge>
                      </div>
                    </div>
                    <div className="flex gap-1">
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => handleDuplicate(template, e)}
                        className="h-8 w-8 p-0"
                      >
                        <Copy className="w-4 h-4" />
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => handleDelete(template, e)}
                        className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </CardHeader>
                <CardContent className="pt-2 pb-6 px-6">
                  {/* Template Preview */}
                  <div className="mb-4 flex items-center justify-center">
                    <TemplatePreviewThumbnail
                      template={template}
                      size="medium"
                      interactive={true}
                    />
                  </div>

                  {/* Template Info */}
                  <div className="flex items-center justify-between text-xs text-gray-500">
                    <div className="flex flex-col gap-1">
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>Created {formatDate(template.createdAt)}</span>
                      </div>
                      <div className="flex items-center gap-1">
                        <Calendar className="w-3 h-3" />
                        <span>Updated {formatDate(template.updatedAt)}</span>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => {
                        e.stopPropagation();
                        onSelectTemplate(template);
                      }}
                      className="h-7 px-2 text-xs self-end"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Edit
                    </Button>
                  </div>
                </CardContent>
              </Card>
              ))}
            </div>
          </div>
        )}

        {/* Other Templates Section */}
        <div>
          <h2 className="text-lg font-semibold text-gray-900 mb-3">All Templates</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Create New Template Card */}
            <Card 
              className="border-2 border-dashed border-gray-300 hover:border-[#183C2E] cursor-pointer transition-colors"
              onClick={onCreateNew}
            >
              <CardContent className="flex flex-col items-center justify-center py-12">
                <Plus className="w-12 h-12 text-gray-400 mb-3" />
                <h3 className="text-lg font-medium text-gray-700">Create New Template</h3>
                <p className="text-sm text-gray-500 mt-1">Start with a blank canvas</p>
              </CardContent>
            </Card>

            {/* Other Templates */}
            {otherTemplates.map((template) => (
          <Card
            key={template.id}
            className={`hover:shadow-xl hover:-translate-y-1 transition-all duration-200 ease-out cursor-pointer ${
              template.isDefault ? 'ring-2 ring-[#183C2E] ring-offset-2' : ''
            }`}
            onClick={() => onSelectTemplate(template)}
          >
            <CardHeader className="pb-4">
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <CardTitle className="text-base line-clamp-1" title={template.name}>
                      {template.name}
                    </CardTitle>
                    {template.isCorporateDefault && (
                      <Badge className="bg-[#183C2E] text-white text-xs">
                        CORPORATE DEFAULT
                      </Badge>
                    )}
                    {template.isOrganizationDefault && (
                      <Badge className="bg-orange-600 text-white text-xs">
                        ORGANIZATION DEFAULT
                      </Badge>
                    )}
                    {userDefaultTemplateId === template.id && (
                      <Badge className="bg-blue-600 text-white text-xs">
                        MY DEFAULT
                      </Badge>
                    )}
                  </div>
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="secondary" className="text-xs font-mono">
                      ID: {template.id.substring(0, 8)}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {template.pageSize === 'letter' ? '8.5" × 11"' : 'A4'}
                    </Badge>
                    <Badge variant="outline" className="text-xs">
                      {template.elements.length} elements
                    </Badge>
                  </div>
                </div>
                <div className="flex gap-1">
                  {userDefaultTemplateId !== template.id && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => handleSetAsDefault(template, e)}
                      className="h-8 w-8 p-0"
                      title="Set as my default template"
                    >
                      <Star className="w-4 h-4" />
                    </Button>
                  )}
                  {userDefaultTemplateId === template.id && (
                    <Button
                      size="sm"
                      variant="ghost"
                      onClick={(e) => handleUnsetDefault(e)}
                      className="h-8 w-8 p-0"
                      title="Remove as my default template"
                    >
                      <Star className="w-4 h-4 fill-blue-600 text-blue-600" />
                    </Button>
                  )}
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => handleDuplicate(template, e)}
                    className="h-8 w-8 p-0"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    onClick={(e) => handleDelete(template, e)}
                    className="h-8 w-8 p-0 text-red-600 hover:text-red-700"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </CardHeader>
            <CardContent className="pt-2 pb-6 px-6">
              {/* Template Preview */}
              <div className="mb-4 flex items-center justify-center">
                <TemplatePreviewThumbnail
                  template={template}
                  size="medium"
                  interactive={true}
                />
              </div>

              {/* Template Info */}
              <div className="flex items-center justify-between text-xs text-gray-500">
                <div className="flex flex-col gap-1">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>Created {formatDate(template.createdAt)}</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <Calendar className="w-3 h-3" />
                    <span>Updated {formatDate(template.updatedAt)}</span>
                  </div>
                </div>
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={(e) => {
                    e.stopPropagation();
                    onSelectTemplate(template);
                  }}
                  className="h-7 px-2 text-xs self-end"
                >
                  <Edit className="w-3 h-3 mr-1" />
                  Edit
                </Button>
              </div>
            </CardContent>
          </Card>
            ))}
          </div>
        </div>
      </div>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={!!deleteTemplate} onOpenChange={() => setDeleteTemplate(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Template</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{deleteTemplate?.name}"? 
              <br />
              <span className="text-xs text-gray-500">Template ID: {deleteTemplate?.id}</span>
              <br />
              This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
};